import React from 'react';

import { useThemeContext } from '$/components/ui/ThemeContext';

import { CancelReason, useResubscribeSubscriptionMutation } from '$/GraphQL/schema/schema';

import { CancelReasonModal } from './CancelReasonModal';
import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import RestoreAccountModal from '../../HomePage/RestoreAccountModal/RestoreAccountModal';
import { UpdateBillingInfoModal } from '$/components/UpdateBillingInfoModal';
import { ApolloError } from '@apollo/client';
import Button from '$/components/ui/Button';
import { SubscriptionInfo } from './SubscriptionInfo';

export enum PlanPageModals {
  Restore = 'Restore',
  UpdateBillingInfo = 'UpdateBillingInfo',
  CancelSubscription = 'CancelSubscription',
  CancelReason = 'CancelReason',
  ViewPlans = 'ViewPlans',
  UpdatePaymentDetails = 'UpdatePaymentDetails',
  UpdateBillingAddress = 'UpdateBillingAddress',
}

interface PlanPageCTAButtons {
  cancel: React.ReactElement;
  restore: React.ReactElement;
  resubscribe: React.ReactElement;
  updateBillingAddress: React.ReactElement;
  updatePaymentDetails: React.ReactElement;
}

interface PlanPageCTAsContextValue {
  buttons: PlanPageCTAButtons;
  error?: ApolloError;
  toggleModal: (modal: PlanPageModals) => void;
}

const PlanPageCTAButtonsContext = React.createContext<PlanPageCTAsContextValue>(
  {} as PlanPageCTAsContextValue
);

export const usePlanPageCTAButtons = (): PlanPageCTAsContextValue =>
  React.useContext(PlanPageCTAButtonsContext);

export const PlanPageCTAButtonsProvider: React.FC<{
  children?: React.ReactNode;
  subscriptionInfo?: SubscriptionInfo;
}> = ({ children, subscriptionInfo }) => {
  const [cancelReasonOption, setCancelReasonOption] = React.useState<CancelReason | undefined>(
    undefined
  );
  const [openModal, setOpenModal] = React.useState<PlanPageModals | null>(null);
  const toggleModal = React.useCallback(
    (modal: PlanPageModals): void => {
      setOpenModal(openModal ? null : modal);
    },
    [openModal, setOpenModal]
  );

  const closeModal = React.useCallback(() => setOpenModal(null), []);

  const [resubscribeSubscriptionMutation, resubscribeSubscriptionMutationResult] =
    useResubscribeSubscriptionMutation();
  const etheme = useThemeContext();

  const handleResubscribe = React.useCallback(async (): Promise<void> => {
    try {
      await resubscribeSubscriptionMutation();
      // delay a little bit for the webhook done
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      console.error(e);
    }
  }, [resubscribeSubscriptionMutation]);

  const buttons = React.useMemo((): PlanPageCTAButtons => {
    const cancel = (
      <Button.redText
        className='flex h-2_5e'
        onClick={(): void => toggleModal(PlanPageModals.CancelReason)}
      >
        Cancel my subscription
      </Button.redText>
    );

    const resubscribe = (
      <Button.submit
        className='flex'
        button={Button.bordered}
        theme={etheme.controls.button.branded}
        submitting={resubscribeSubscriptionMutationResult.loading}
        onClick={handleResubscribe}
      >
        Resubscribe now
      </Button.submit>
    );

    const restore = (
      <Button.bordered
        className='flex mt-1e'
        onClick={(): void => toggleModal(PlanPageModals.Restore)}
      >
        Resubscribe now
      </Button.bordered>
    );

    const updatePaymentDetails = (
      <Button.bordered
        className='flex mt-1e'
        onClick={(): void => toggleModal(PlanPageModals.UpdatePaymentDetails)}
      >
        Update payment details
      </Button.bordered>
    );

    const updateBillingAddress = (
      <Button.blueText
        className='flex mt-1e'
        onClick={(): void => toggleModal(PlanPageModals.UpdateBillingAddress)}
      >
        Edit billing address
      </Button.blueText>
    );

    return {
      cancel,
      restore,
      resubscribe,
      updateBillingAddress,
      updatePaymentDetails,
    };
  }, [
    toggleModal,
    handleResubscribe,
    etheme.controls.button.branded,
    resubscribeSubscriptionMutationResult.loading,
  ]);

  const modal = React.useMemo(() => {
    switch (openModal) {
      case PlanPageModals.CancelSubscription:
        if (!subscriptionInfo?.nextPaymentDueDate) return null;
        return (
          <CancelSubscriptionModal
            toggleModal={closeModal}
            nextPaymentDueDate={subscriptionInfo.nextPaymentDueDate}
            cancelReasonOption={cancelReasonOption}
          />
        );
      case PlanPageModals.CancelReason:
        return (
          <CancelReasonModal
            toggleModal={closeModal}
            onOpenCancelSubscriptionModal={(): void => {
              setOpenModal(PlanPageModals.CancelSubscription);
            }}
            setCancelReasonOption={setCancelReasonOption}
            cancelReasonOption={cancelReasonOption}
          />
        );
      case PlanPageModals.Restore:
        return <RestoreAccountModal onClose={closeModal} />;
      case PlanPageModals.UpdatePaymentDetails:
        return <UpdateBillingInfoModal toggleModal={closeModal} />;
      case PlanPageModals.UpdateBillingAddress:
        return <UpdateBillingInfoModal updateBillingAddress toggleModal={closeModal} />;
    }
    return null;
  }, [openModal, closeModal, subscriptionInfo?.nextPaymentDueDate, cancelReasonOption]);

  return (
    <PlanPageCTAButtonsContext.Provider
      value={{ buttons, error: resubscribeSubscriptionMutationResult.error, toggleModal }}
    >
      <>{children}</>
      {modal}
    </PlanPageCTAButtonsContext.Provider>
  );
};
